import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { Button, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, money } from '../../lib/format.ts';
import { usePlaces } from '../../lib/places.tsx';
import type { FiscalCheck, FiscalState, FiscalStatus } from '../../lib/types.ts';
import { ManageLayout, useAction, useSave } from './common.tsx';

/** Codes from the tax office: two letters, three digits, two letters, three digits. */
const CODE = /^[a-z]{2}\d{3}[a-z]{2}\d{3}$/;
const clean = (v: string) => v.trim().toLowerCase();

const CHECKS: Record<FiscalCheck, { label: MessageKey; fix: MessageKey; to?: string; anchor?: string }> = {
  company_tin: { label: 'fiscal.check_company_tin', fix: 'fiscal.fix_company_tin', to: '/manage/business' },
  business_unit: { label: 'fiscal.check_business_unit', fix: 'fiscal.fix_codes', anchor: 'fiscal-codes' },
  software: { label: 'fiscal.check_software', fix: 'fiscal.fix_codes', anchor: 'fiscal-codes' },
  maintainer: { label: 'fiscal.check_maintainer', fix: 'fiscal.fix_codes', anchor: 'fiscal-codes' },
  registers: { label: 'fiscal.check_registers', fix: 'fiscal.fix_registers', anchor: 'fiscal-registers' },
  operators: { label: 'fiscal.check_operators', fix: 'fiscal.fix_operators', anchor: 'fiscal-operators' },
};

const STATE: Record<FiscalState, { tone: 'ok' | 'warn' | 'danger'; icon: 'checkCircle' | 'clock' | 'alert'; label: MessageKey }> = {
  fiscalized: { tone: 'ok', icon: 'checkCircle', label: 'fiscal.state_fiscalized' },
  pending: { tone: 'warn', icon: 'clock', label: 'fiscal.state_pending' },
  rejected: { tone: 'danger', icon: 'alert', label: 'fiscal.state_rejected' },
};

/** Albanian fiscalization: the codes from the tax office, what is still missing, and the send queue. */
export function Fiscal() {
  const { data, reload } = useAction<FiscalStatus>('fiscal_al.status');
  const { busy: sending, run } = useSave();

  if (!data) {
    return (
      <ManageLayout title={t('manage.fiscal')} subtitle={t('fiscal.subtitle')}>
        <Skeleton h={480} r="var(--radius-lg)" />
      </ManageLayout>
    );
  }
  const { settings } = data;
  // Receipts and invoices to businesses go through the same queue.
  const inv = data.invoices;
  const dn = data.deliveries;
  const queue = {
    ...data.queue,
    pending: data.queue.pending + (inv?.pending ?? 0) + (dn?.pending ?? 0),
    rejected: data.queue.rejected + (inv?.rejected ?? 0) + (dn?.rejected ?? 0),
    overdue: data.queue.overdue + (inv?.overdue ?? 0) + (dn?.overdue ?? 0),
  };
  const sendNow = () =>
    void run(async () => {
      const r = await action<{ sent: number; failed: number; rejected: number }>('fiscal_al.send_now');
      await reload();
      return r;
    }, t('fiscal.send_done'));

  return (
    <ManageLayout title={t('manage.fiscal')} subtitle={t('fiscal.subtitle')}>
      <div className="fiscal">
        <section className="card fiscal-state" aria-labelledby="fiscal-state-title">
          <div className="card-head">
            <div className="fiscal-state-main">
              <span className={`fiscal-state-icon ${settings.enabled ? 'on' : ''}`} aria-hidden="true">
                <Icon name={settings.enabled ? 'shieldCheck' : 'shield'} size={24} />
              </span>
              <div>
                <h2 id="fiscal-state-title">{settings.enabled ? t('fiscal.on_title') : t('fiscal.off_title')}</h2>
                <p className="muted">{settings.enabled ? t('fiscal.on_body') : t('fiscal.off_body')}</p>
              </div>
            </div>
            <StatusBadge
              tone={settings.enabled ? 'ok' : 'neutral'}
              icon={settings.enabled ? 'checkCircle' : 'pause'}
              label={settings.enabled ? t('fiscal.on') : t('fiscal.off')}
            />
          </div>
          {data.transport.test ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={18} /> {t('fiscal.test_mode')}
            </p>
          ) : null}
          <dl className="fiscal-stats">
            <Stat label={t('fiscal.stat_pending')} value={queue.pending} tone={queue.pending ? 'warn' : undefined} />
            <Stat label={t('fiscal.stat_rejected')} value={queue.rejected} tone={queue.rejected ? 'danger' : undefined} />
            <Stat
              label={t('fiscal.stat_overdue', { hours: queue.late_after_hours })}
              value={queue.overdue}
              tone={queue.overdue ? 'danger' : undefined}
            />
            <Stat label={t('fiscal.stat_sent')} value={queue.sent_today} />
          </dl>
          <div className="fiscal-state-actions">
            <span className="muted">{t('fiscal.via', { name: data.transport.name })}</span>
            <Button icon="send" onClick={sendNow} busy={sending} disabled={queue.pending === 0 && data.deposits.every((d) => d.state !== 'pending')}>
              {t('fiscal.send_now')}
            </Button>
          </div>
        </section>

        {data.waiting.length || data.deposits.length || inv?.waiting.length ? <Queue data={data} onChange={reload} /> : null}

        <section className="list-card" aria-labelledby="fiscal-steps-title">
          <div className="list-card-head">
            <div>
              <h2 id="fiscal-steps-title">{settings.enabled ? t('fiscal.steps_on') : t('fiscal.steps')}</h2>
              <p className="muted">{data.ready ? t('fiscal.steps_ready') : t('fiscal.steps_hint')}</p>
            </div>
          </div>
          {data.checks.map((c) => {
            const meta = CHECKS[c.key];
            return (
              <div key={c.key} className="list-row">
                <span className={`check-mark ${c.ok ? 'ok' : 'todo'}`} aria-hidden="true">
                  <Icon name={c.ok ? 'check' : 'circle'} size={16} />
                </span>
                <span className="list-main">
                  <span className="list-title">{t(meta.label)}</span>
                  {!c.ok ? (
                    <span className="list-sub">
                      {c.missing?.length ? t('fiscal.missing_for', { names: c.missing.join(', ') }) : t(meta.fix)}
                    </span>
                  ) : null}
                </span>
                <StatusBadge tone={c.ok ? 'ok' : 'warn'} icon={c.ok ? 'check' : 'clock'} label={c.ok ? t('fiscal.done') : t('fiscal.to_do')} />
                {!c.ok && meta.to ? (
                  <Link className="btn btn-ghost btn-sm" to={meta.to}>
                    {t('fiscal.open')}
                  </Link>
                ) : !c.ok && meta.anchor ? (
                  <a className="btn btn-ghost btn-sm" href={`#${meta.anchor}`}>
                    {t('fiscal.go')}
                  </a>
                ) : null}
              </div>
            );
          })}
        </section>

        <Settings status={data} onSaved={reload} />

        <section className="list-card" id="fiscal-registers" aria-labelledby="fiscal-registers-title">
          <div className="list-card-head">
            <div>
              <h2 id="fiscal-registers-title">{t('fiscal.registers')}</h2>
              <p className="muted">{t('fiscal.registers_hint')}</p>
            </div>
          </div>
          {data.registers.map((r) => (
            <CodeRow
              key={r.id}
              icon="register"
              title={r.name}
              label={t('fiscal.tcr_for', { name: r.name })}
              value={r.tcr_code}
              onSave={(code) => action('fiscal_al.set_register_code', { register: r.id, tcr_code: code })}
              onSaved={reload}
            />
          ))}
        </section>

        <section className="list-card" id="fiscal-operators" aria-labelledby="fiscal-operators-title">
          <div className="list-card-head">
            <div>
              <h2 id="fiscal-operators-title">{t('fiscal.operators')}</h2>
              <p className="muted">{t('fiscal.operators_hint')}</p>
            </div>
          </div>
          {data.operators.map((o) => (
            <CodeRow
              key={o.user}
              icon="users"
              title={o.name}
              sub={o.username}
              label={t('fiscal.operator_for', { name: o.name })}
              value={o.code}
              onSave={(code) => action('fiscal_al.set_operator_code', { user: o.user, code })}
              onSaved={reload}
            />
          ))}
        </section>
      </div>
    </ManageLayout>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warn' | 'danger' }) {
  return (
    <div className={`fiscal-stat${tone ? ` ${tone}` : ''}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Receipts and cash declarations that have not reached the tax office yet. */
function Queue({ data, onChange }: { data: FiscalStatus; onChange: () => Promise<unknown> }) {
  const { run } = useSave();
  const retry = (input: { invoice: string } | { deposit: string } | { noncash: string } | { einvoice: string } | { wtn: string }) =>
    void run(async () => {
      await action('fiscal_al.retry', input);
      await onChange();
    }, t('fiscal.retried'));
  return (
    <section className="list-card" aria-labelledby="fiscal-queue-title">
      <div className="list-card-head">
        <div>
          <h2 id="fiscal-queue-title">{t('fiscal.queue')}</h2>
          <p className="muted">{t('fiscal.queue_hint', { hours: data.queue.late_after_hours })}</p>
        </div>
      </div>
      {data.deposits.map((d) => (
        <div key={d.id} className="list-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name="cash" size={20} />
          </span>
          <span className="list-main">
            <span className="list-title">
              {t(d.operation === 'initial' ? 'fiscal.deposit_initial' : 'fiscal.deposit_withdraw', { name: d.register_name })} · {money(d.amount)}
            </span>
            <span className="list-sub">
              {dateTime(d.changed_at)}
              {d.attempts ? ` · ${tn('fiscal.attempts', d.attempts)}` : ''}
              {d.last_error ? ` · ${d.last_error}` : ''}
            </span>
          </span>
          <StatusBadge {...STATE[d.state]} label={t(STATE[d.state].label)} />
          {d.state === 'rejected' ? (
            <Button icon="send" onClick={() => retry({ deposit: d.id })}>
              {t('fiscal.retry')}
            </Button>
          ) : null}
        </div>
      ))}
      {data.waiting.map((w) => (
        <div key={w.id} className="list-row">
          <span className="list-icon receipt-no" aria-hidden="true">
            {w.receipt_no}
          </span>
          <span className="list-main">
            <span className="list-title">
              {w.inv_num} · {w.order_name}
            </span>
            <span className="list-sub">
              {dateTime(w.issued_at)}
              {w.attempts ? ` · ${tn('fiscal.attempts', w.attempts)}` : ''}
              {w.last_error ? ` · ${w.last_error}` : ''}
            </span>
          </span>
          <span className="list-price">{money(w.total)}</span>
          <StatusBadge {...STATE[w.state]} label={t(STATE[w.state].label)} />
          {w.state === 'rejected' ? (
            <Button icon="send" onClick={() => retry({ invoice: w.id })}>
              {t('fiscal.retry')}
            </Button>
          ) : null}
        </div>
      ))}
      {(data.invoices?.waiting ?? []).map((w) => (
        <div key={w.einvoice ?? w.id} className="list-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name={w.kind === 'credit' ? 'undo' : 'invoice'} size={20} />
          </span>
          <span className="list-main">
            <span className="list-title">
              {t(w.einvoice ? 'fiscal.einvoice_of' : w.kind === 'credit' ? 'fiscal.credit_of' : 'fiscal.invoice_of', { number: w.inv_num })} · {w.customer_name}
            </span>
            <span className="list-sub">
              {dateTime(w.issued_at)}
              {w.attempts ? ` · ${tn('fiscal.attempts', w.attempts)}` : ''}
              {w.last_error ? ` · ${w.last_error}` : ''}
            </span>
          </span>
          <span className="list-price">{money(w.total)}</span>
          <StatusBadge {...STATE[w.state]} label={t(STATE[w.state].label)} />
          {w.state === 'rejected' ? (
            <Button icon="send" onClick={() => retry(w.einvoice ? { einvoice: w.einvoice } : { noncash: w.id })}>
              {t('fiscal.retry')}
            </Button>
          ) : null}
        </div>
      ))}
      {(data.deliveries?.waiting ?? []).map((w) => (
        <div key={w.id} className="list-row">
          <span className="list-icon" aria-hidden="true">
            <Icon name="truck" size={20} />
          </span>
          <span className="list-main">
            <span className="list-title">
              {t('fiscal.wtn_of', { number: w.wtn_num })} · {w.customer_name}
            </span>
            <span className="list-sub">
              {dateTime(w.issued_at)}
              {w.attempts ? ` · ${tn('fiscal.attempts', w.attempts)}` : ''}
              {w.last_error ? ` · ${w.last_error}` : ''}
            </span>
          </span>
          <StatusBadge {...STATE[w.state]} label={t(STATE[w.state].label)} />
          {w.state === 'rejected' ? (
            <Button icon="send" onClick={() => retry({ wtn: w.id })}>
              {t('fiscal.retry')}
            </Button>
          ) : null}
        </div>
      ))}
    </section>
  );
}

/** The codes the tax office gave the business, and the switch that turns fiscalization on. */
function Settings({ status, onSaved }: { status: FiscalStatus; onSaved: () => Promise<unknown> }) {
  const s = status.settings;
  const initial = {
    business_unit_code: s.business_unit_code ?? '',
    software_code: s.software_code ?? '',
    maintainer_code: s.maintainer_code ?? '',
    issuer_in_vat: s.issuer_in_vat,
    invoice_tcr_code: s.invoice_tcr_code ?? '',
  };
  const [form, setForm] = useState(initial);
  const [enabled, setEnabled] = useState(s.enabled);
  const { busy, run } = useSave();
  const { multi } = usePlaces();
  const key = JSON.stringify(initial);
  useEffect(() => {
    setForm(JSON.parse(key));
    setEnabled(s.enabled);
  }, [key, s.enabled]);

  const error = (v: string) => (v && !CODE.test(clean(v)) ? t('fiscal.code_rule') : null);
  const codes = ['business_unit_code', 'software_code', 'maintainer_code'] as const;
  const invalid = codes.some((k) => error(form[k])) || !!error(form.invoice_tcr_code);
  const dirty = JSON.stringify(form) !== key || enabled !== s.enabled;
  const blocked = enabled && !s.enabled && !status.ready && codes.some((k) => !form[k]);

  const save = () => {
    if (invalid) return;
    void run(async () => {
      await action('fiscal_al.update_settings', {
        enabled,
        issuer_in_vat: form.issuer_in_vat,
        ...Object.fromEntries(codes.map((k) => [k, clean(form[k])])),
        ...(status.invoices ? { invoice_tcr_code: clean(form.invoice_tcr_code) } : {}),
      });
      await onSaved();
    }, enabled ? t('fiscal.saved_on') : t('fiscal.saved'));
  };

  const labels: Record<(typeof codes)[number], [MessageKey, MessageKey]> = {
    business_unit_code: ['fiscal.business_unit', 'fiscal.business_unit_hint'],
    software_code: ['fiscal.software', 'fiscal.software_hint'],
    maintainer_code: ['fiscal.maintainer', 'fiscal.maintainer_hint'],
  };

  return (
    <form
      className="card"
      id="fiscal-codes"
      aria-labelledby="fiscal-codes-title"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <div className="card-head">
        <div>
          <h2 id="fiscal-codes-title">{t('fiscal.codes')}</h2>
          <p className="muted">{t('fiscal.codes_hint')}</p>
        </div>
      </div>
      <div className="form-grid">
        {codes.map((k) => (
          <Field key={k} label={t(labels[k][0])} hint={k === 'business_unit_code' && multi ? t('fiscal.business_unit_hint_places') : t(labels[k][1])} error={error(form[k])}>
            <input
              className="input mono"
              value={form[k]}
              maxLength={10}
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              placeholder={t('fiscal.code_placeholder')}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          </Field>
        ))}
        {status.invoices ? (
          <Field label={t('fiscal.invoice_tcr')} hint={t('fiscal.invoice_tcr_hint')} error={error(form.invoice_tcr_code)}>
            <input
              className="input mono"
              value={form.invoice_tcr_code}
              maxLength={10}
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              placeholder={t('fiscal.code_placeholder')}
              onChange={(e) => setForm({ ...form, invoice_tcr_code: e.target.value })}
            />
          </Field>
        ) : null}
      </div>
      <div className="fiscal-switches">
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('fiscal.in_vat')}</span>
            <span className="muted">{t('fiscal.in_vat_hint')}</span>
          </span>
          <Switch checked={form.issuer_in_vat} label={t('fiscal.in_vat')} onChange={(v) => setForm({ ...form, issuer_in_vat: v })} />
        </label>
        <label className="switch-row">
          <span>
            <span className="switch-title">{t('fiscal.enable')}</span>
            <span className="muted">{status.ready || s.enabled ? t('fiscal.enable_hint') : t('fiscal.enable_blocked')}</span>
          </span>
          <Switch checked={enabled} label={t('fiscal.enable')} onChange={setEnabled} />
        </label>
      </div>
      <div className="form-actions">
        <Button type="submit" variant="primary" icon="check" busy={busy} disabled={!dirty || invalid || blocked}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

/** One register or person with its code from the tax office, saved on its own. */
function CodeRow({
  icon,
  title,
  sub,
  label,
  value,
  onSave,
  onSaved,
}: {
  icon: 'register' | 'users';
  title: string;
  sub?: string;
  label: string;
  value: string | null;
  onSave: (code: string | null) => Promise<unknown>;
  onSaved: () => Promise<unknown>;
}) {
  const [code, setCode] = useState(value ?? '');
  const { busy, run } = useSave();
  useEffect(() => setCode(value ?? ''), [value]);
  const next = clean(code);
  const bad = !!next && !CODE.test(next);
  const changed = next !== (value ?? '');
  const save = () => {
    if (bad || !changed) return;
    void run(async () => {
      await onSave(next || null);
      await onSaved();
    }, next ? t('fiscal.code_saved', { name: title }) : t('fiscal.code_removed', { name: title }));
  };
  return (
    <form
      className="list-row code-row"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <span className="list-icon" aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <span className="list-main">
        <span className="list-title">{title}</span>
        {value ? (
          <span className="list-sub">{sub ?? t('fiscal.has_code')}</span>
        ) : (
          <span className="list-sub">
            <StatusBadge tone="warn" icon="clock" label={t('fiscal.no_code')} />
          </span>
        )}
      </span>
      <span className="code-input">
        <input
          className={`input mono${bad ? ' invalid' : ''}`}
          aria-label={label}
          aria-invalid={bad || undefined}
          value={code}
          maxLength={10}
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          placeholder={t('fiscal.code_placeholder')}
          onChange={(e) => setCode(e.target.value)}
        />
        {bad ? <span className="field-msg" role="alert">{t('fiscal.code_rule')}</span> : null}
      </span>
      <Button type="submit" icon="check" busy={busy} disabled={!changed || bad}>
        {t('common.save')}
      </Button>
    </form>
  );
}
